import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tabs,
  Tab,
  Box,
  TextField,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { DispersalRow, TimelineStep, DispersalsResponse } from '../types';

const statusColor: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  APPROVED_BY_TEACHER: 'info',
  APPROVED_BY_PRINCIPAL: 'info',
  COMPLETED: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
};

function EarlyStatusTimeline({ d }: { d: DispersalRow }) {
  const status = d.status;
  const teacherName = d.approvedBy?.teacher?.name;
  const principalName = d.approvedBy?.principal?.name;
  const step1Done = true;
  const step2Done = !!teacherName || (status !== 'PENDING' && status !== 'CANCELLED' && status !== 'REJECTED');
  const step3Done = !!principalName || status === 'APPROVED_BY_PRINCIPAL' || status === 'COMPLETED';

  let steps: TimelineStep[] = [
    { label: 'Early dispersal requested', done: step1Done },
    {
      label: step2Done ? `Approved by class teacher${teacherName ? ` (${teacherName})` : ''}` : 'Waiting for class teacher to approve',
      done: step2Done,
    },
    {
      label: step3Done ? `Approved by principal${principalName ? ` (${principalName})` : ''}` : 'Waiting for principal to approve',
      done: step3Done,
    },
  ];

  if (status === 'CANCELLED') {
    const by = d.cancelledBy?.name || '–';
    const cancelComment = d.cancellationComment?.trim();
    steps = [...steps, { label: `Cancelled (by ${by})`, subLabel: cancelComment || undefined, done: true, variant: 'cancelled' }];
  } else if (status === 'REJECTED') {
    const by = d.rejectedBy?.name;
    const reason = d.rejectionReason?.trim();
    steps = [...steps, { label: by ? `Rejected (by ${by})` : 'Rejected', subLabel: reason || undefined, done: true, variant: 'rejected' }];
  }

  const getDotSx = (step: TimelineStep) => {
    if (step.variant === 'cancelled') return { bgcolor: 'text.secondary', border: '2px solid', borderColor: 'text.secondary', boxShadow: 'none' };
    if (step.variant === 'rejected') return { bgcolor: 'error.main', border: '2px solid', borderColor: 'error.dark', boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.2)' };
    if (step.done) return { bgcolor: 'success.main', border: '2px solid', borderColor: 'success.dark', boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.2)' };
    return { bgcolor: 'warning.main', border: '2px solid', borderColor: 'warning.dark', boxShadow: '0 0 0 2px rgba(237, 108, 2, 0.2)' };
  };

  const getLabelColor = (step: TimelineStep) => {
    if (step.variant === 'cancelled') return 'text.secondary';
    if (step.variant === 'rejected') return 'error.main';
    return step.done ? 'success.dark' : 'text.secondary';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5, position: 'relative', pl: 1 }}>
      <Box sx={{ position: 'absolute', left: 12, top: 10, bottom: 10, width: 2, bgcolor: 'action.selected', borderRadius: 1 }} />
      {steps.map((step, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ flexShrink: 0, width: 10, height: 10, borderRadius: '50%', mt: 0.6, ...getDotSx(step) }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: step.done ? 600 : 500, color: getLabelColor(step) }}>
              {step.label}
            </Typography>
            {step.subLabel && (
              <Tooltip title={step.subLabel} placement="top" arrow>
                <CommentOutlinedIcon sx={{ fontSize: 16, color: getLabelColor(step), cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const StaffDispersals: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getStoredUser();
  const isPrincipal = user && canAccessPrincipalFeatures(user.role);
  const { canView, loading: featureLoading } = useFeatureAccess('dispersals');
  const today = new Date().toISOString().slice(0, 10);
  const isEarlyTab = location.pathname.endsWith('/early');
  const tab = isEarlyTab ? 1 : 0;
  const [listDate, setListDate] = useState(today);
  const [rejectDispersal, setRejectDispersal] = useState<DispersalRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const setTabByIndex = (index: number) => {
    if (index === 0) navigate('/dispersals');
    else navigate('/dispersals/early');
  };

  React.useEffect(() => {
    if (featureLoading) return;
    if (!isPrincipal && !canView) navigate('/students', { replace: true });
  }, [isPrincipal, canView, featureLoading, navigate]);

  const { data, isLoading } = useQuery<DispersalsResponse>(
    ['dispersals', listDate],
    () => apiClient.get(`/dispersals?date=${listDate}`).then((r: { data: unknown }) => r.data as DispersalsResponse)
  );
  const dispersals: DispersalRow[] = data?.dispersals ?? [];
  const earlyDispersals = dispersals.filter((d: DispersalRow) => d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL');
  const gateDispersalsRaw = dispersals.filter((d: DispersalRow) => d.requestType === 'QR_SCAN' || d.requestSource === 'MANUAL_REQUEST' || d.requestSource === 'QR_SCAN' || (!d.requestType));
  const getRequestCount = (d: DispersalRow) => d.requestCount ?? 1;
  const gateDispersals = [...gateDispersalsRaw].sort((a, b) => {
    const countA = getRequestCount(a);
    const countB = getRequestCount(b);
    if (countB !== countA) return countB - countA;
    return 0;
  });
  const isPending = (d: DispersalRow) => d.status === 'PENDING' || d.status === 'APPROVED_BY_TEACHER';
  const dispersalPendingCount = gateDispersals.filter(isPending).length;
  const earlyPendingCount = earlyDispersals.filter(isPending).length;

  const sendMutation = useMutation(
    (dispersalId: string) => apiClient.put(`/dispersals/${dispersalId}/send-from-class`).then((r: { data: unknown }) => r.data),
    { onSuccess: () => queryClient.invalidateQueries('dispersals') }
  );

  const approveTeacherMutation = useMutation(
    (dispersalId: string) => apiClient.put(`/dispersals/${dispersalId}/approve/teacher`).then((r: { data: unknown }) => r.data),
    { onSuccess: () => queryClient.invalidateQueries('dispersals') }
  );

  const approvePrincipalMutation = useMutation(
    (dispersalId: string) => apiClient.put(`/dispersals/${dispersalId}/approve/principal`).then((r: { data: unknown }) => r.data),
    { onSuccess: () => queryClient.invalidateQueries('dispersals') }
  );

  const rejectMutation = useMutation(
    ({ dispersalId, reason }: { dispersalId: string; reason: string }) =>
      apiClient.put(`/dispersals/${dispersalId}/reject`, { reason }).then((r: { data: unknown }) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dispersals');
        setRejectDispersal(null);
        setRejectReason('');
      },
    }
  );

  const canSend = (d: DispersalRow) =>
    d.status === 'APPROVED_BY_TEACHER' ||
    d.status === 'APPROVED_BY_PRINCIPAL' ||
    ((d.requestType === 'QR_SCAN' || d.requestSource === 'QR_SCAN' || d.requestSource === 'MANUAL_REQUEST') && d.status === 'PENDING');

  const canApproveTeacher = (d: DispersalRow) => (d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') && d.status === 'PENDING';
  const canApprovePrincipal = (d: DispersalRow) =>
    (d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') && (d.status === 'PENDING' || d.status === 'APPROVED_BY_TEACHER');
  const canReject = (d: DispersalRow) =>
    (d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') && (d.status === 'PENDING' || d.status === 'APPROVED_BY_TEACHER');

  if (isLoading) return <Box><Typography>Loading…</Typography></Box>;

  const renderTable = (list: DispersalRow[], showEarlyActions = false) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Student</TableCell>
            <TableCell>Class</TableCell>
            <TableCell>Gate / Time</TableCell>
            <TableCell>Times requested</TableCell>
            <TableCell>Requested by</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography color="text.secondary">
                  {showEarlyActions ? 'No early dispersal requests.' : 'No dispersals for this date.'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            list.map((d) => (
              <TableRow key={d._id}>
                <TableCell>
                  {(d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') ? <Chip label="Early" size="small" /> : 'Gate'}
                </TableCell>
                <TableCell>{d.studentId?.name}</TableCell>
                <TableCell>{d.studentId?.class}{d.studentId?.section ? ` ${d.studentId.section}` : ''}</TableCell>
                <TableCell>
                  {d.gateName || (d.dispersalTime || d.dismissalTime ? new Date(d.dispersalTime || d.dismissalTime!).toLocaleString() : '–')}
                  {d.requestSource === 'MANUAL_REQUEST' && <Chip label="Request" size="small" sx={{ ml: 0.5 }} />}
                  {d.requestSource === 'QR_SCAN' && d.gateName && <Chip label="QR scan" size="small" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell>
                  {showEarlyActions ? '–' : getRequestCount(d)}
                </TableCell>
                <TableCell>{d.requestedBy?.name}</TableCell>
                <TableCell>
                  {showEarlyActions ? (
                    <EarlyStatusTimeline d={d} />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={d.status} size="small" color={(d.status && statusColor[d.status]) || 'default'} />
                        {d.status === 'REJECTED' && d.rejectionReason && (
                          <Tooltip title={d.rejectionReason} placement="top" arrow>
                            <CommentOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        )}
                        {d.status === 'CANCELLED' && d.cancellationComment && (
                          <Tooltip title={d.cancellationComment} placement="top" arrow>
                            <CommentOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                      {d.pickupConfirmed && (
                        <Typography variant="caption" color="text.secondary">
                          {d.autoConfirmed
                            ? `Auto confirmed${d.pickupConfirmedAt ? ` • ${new Date(d.pickupConfirmedAt).toLocaleString()}` : ''}`
                            : `Confirmed by: ${d.pickupConfirmedBy?.name || d.requestedBy?.name || 'Guardian'}${d.pickupConfirmedAt ? ` • ${new Date(d.pickupConfirmedAt).toLocaleString()}` : ''}`}
                        </Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">
                  {showEarlyActions && canApproveTeacher(d) && user?.role === 'TEACHER' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => approveTeacherMutation.mutate(d._id)}
                      disabled={approveTeacherMutation.isLoading}
                      sx={{ mr: 1 }}
                    >
                      Approve (Teacher)
                    </Button>
                  )}
                  {showEarlyActions && canApprovePrincipal(d) && isPrincipal && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => approvePrincipalMutation.mutate(d._id)}
                      disabled={approvePrincipalMutation.isLoading}
                      sx={{ mr: 1 }}
                    >
                      Approve (Principal)
                    </Button>
                  )}
                  {showEarlyActions && canReject(d) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setRejectDispersal(d);
                        setRejectReason('');
                      }}
                      sx={{ mr: 1 }}
                    >
                      Reject
                    </Button>
                  )}
                  {!showEarlyActions && canSend(d) && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => sendMutation.mutate(d._id)}
                      disabled={sendMutation.isLoading}
                    >
                      Sent from class
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Dispersals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dispersal: when a parent requests at gate, send the child from class. Early: approve parent&apos;s early dispersal requests.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          type="date"
          label="Date"
          value={listDate}
          onChange={(e) => setListDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mr: 2 }}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTabByIndex(v)} sx={{ mb: 2 }}>
        <Tab
          label={
            dispersalPendingCount > 0 ? (
              <Badge badgeContent={dispersalPendingCount} color="error">
                <Box component="span" sx={{ pr: 1 }}>Dispersal</Box>
              </Badge>
            ) : (
              'Dispersal'
            )
          }
        />
        <Tab
          label={
            earlyPendingCount > 0 ? (
              <Badge badgeContent={earlyPendingCount} color="error">
                <Box component="span" sx={{ pr: 1 }}>Early dispersal</Box>
              </Badge>
            ) : (
              'Early dispersal'
            )
          }
        />
      </Tabs>

      {tab === 0 && renderTable(gateDispersals)}
      {tab === 1 && renderTable(earlyDispersals, true)}

      <Dialog
        open={!!rejectDispersal}
        onClose={() => !rejectMutation.isLoading && setRejectDispersal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject early dispersal request</DialogTitle>
        <DialogContent>
          {rejectDispersal && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reject request for <strong>{rejectDispersal.studentId?.name}</strong>? Please provide a reason (parent will see it).
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for rejection"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Requested time is after school closing."
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDispersal(null)} disabled={rejectMutation.isLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => rejectDispersal && rejectMutation.mutate({ dispersalId: rejectDispersal._id, reason: rejectReason })}
            disabled={rejectMutation.isLoading || !rejectReason.trim()}
          >
            {rejectMutation.isLoading ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffDispersals;
